import * as A from 'astronomy-engine';
import type { AppServices } from '../app';
import { activeShowers, hourlyRate } from '../astro/meteors';
import { moonPhaseName } from '../astro/solar';
import { starName } from '../data/catalog';
import { ARABIC_STAR_NAMES, BODY_NOTES, constellationMeaning } from '../data/lore';
import { Vec3, enuToAltAz, refractEnu } from '../lib/math';
import { place, settings, type Selection } from '../state';
import { fmtTime } from '../ui/format';
import { MANAZIL, clip } from './narration';
import { knownMeaning, stationOf } from '../data/arabicSky';
import { displayName } from './satellites';
import { crescentDay, lookingFor } from './saudi';

export interface TourStop {
  title: string;
  text: string; // shown on screen (may include times and exact numbers)
  clips: string[]; // narration clip keys (pre-rendered)
  enu: Vec3 | null;
  fov: number;
  sel?: Selection;
}

const DIRS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
const altAz = (enu: Vec3) => enuToAltAz(refractEnu(enu));
const where = (enu: Vec3) => {
  const { alt, az } = altAz(enu);
  if (alt > 70) return 'almost overhead';
  const h = alt > 45 ? 'high' : alt > 20 ? 'halfway up' : 'low';
  return `${h} in the ${DIRS[Math.round(az / 45) % 8]}`;
};
const loc = (enu: Vec3) => {
  const { alt, az } = altAz(enu);
  return clip.location(alt, az).key;
};

/** A short guided tour of what is up at the current simulated time. */
export async function buildTour(s: AppServices, placeName: string, jumped: boolean): Promise<TourStop[]> {
  const e = s.engine;
  // The Arabic names and the Moon's station on screen when Arabic is switched on; never spoken in the tour (rule G9:
  // the Arabic is heard only from the speaker beside it).
  const arabic = settings.peek().arabic;
  const ctx = e.context;
  const date = ctx.date;
  const stops: TourStop[] = [];
  const up = (v: Vec3) => v[2] > Math.sin((12 * Math.PI) / 180);

  stops.push({
    title: 'Tonight’s sky',
    text: `Welcome to tonight’s sky over ${placeName}. Let’s look at the highlights. Tap anything along the way to learn more.`,
    clips: jumped ? [clip.jumpToEvening().key, clip.intro().key] : [clip.intro().key],
    enu: [0, -Math.cos(0.7), Math.sin(0.7)], fov: 110,
  });

  // The Moon.
  const moon = e.bodies.find((b) => b.id === 'Moon')!;
  const moonEnu = e.frame.toEnu(moon.dir);
  // Not on the day Saudi Arabia looks for the crescent: nothing is said of where the Moon is then (src/live/saudi.ts).
  if (up(moonEnu) && !lookingFor(date.getTime(), place.peek())) {
    const phase = moonPhaseName(A.MoonPhase(date));
    const crescent = crescentDay(date.getTime(), place.peek());
    const full = phase === 'Full Moon' || moon.phaseFraction > 0.95;
    // Its station (manzil) among the stars, from the Arabic tradition.
    let station = '';
    try {
      if (arabic) await e.ensureArabic();
      const ar = arabic ? e.arabic : null;
      const k = ar ? e.stars.byHip.get(ar.sky.lunar.anchorHip) : undefined;
      if (ar && k !== undefined) {
        const { index } = stationOf(ar.sky, e.starDir(k), moon.dir);
        const n = ar.sky.lunar.names[index];
        station = ` In the Arabic tradition it is in its station of ${MANAZIL[index]} (${n.ar})${knownMeaning(n.en) ? `, “${n.en.toLowerCase()}”` : ''}.`;
      }
    } catch {
      /* the stations are a bonus */
    }
    stops.push({
      title: 'The Moon',
      // On the 29th, the 30th and the 1st, just "a crescent", and no phase said (src/live/saudi.ts).
      text: crescent
        ? `The Moon is ${where(moonEnu)}: a crescent.${station} ${clip.moonTip(full).text}`
        : `The Moon is ${where(moonEnu)}: ${phase.toLowerCase()}, ${Math.round(moon.phaseFraction * 100)}% lit.${station} ${clip.moonTip(full).text}`,
      clips: [clip.moon().key, ...(crescent ? [] : [clip.moonPhase(phase).key]), loc(moonEnu), clip.moonTip(full).key],
      enu: moonEnu, fov: 8, sel: { kind: 'body', id: 'Moon' },
    });
  }

  // Planets, brightest first.
  let firstPlanet = true;
  for (const p of e.bodies.filter((b) => ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'].includes(b.id)).sort((a, b) => a.mag - b.mag)) {
    const v = e.frame.toEnu(p.dir);
    if (!up(v) || p.mag > 2) continue;
    stops.push({
      title: p.id,
      text: `${p.id} is ${where(v)}, shining with a steady light. ${BODY_NOTES[p.id]}`,
      clips: [clip.planet(p.id).key, loc(v), ...(firstPlanet ? [clip.planetSteady().key] : []), clip.planetFact(p.id, '').key],
      enu: v, fov: 30, sel: { kind: 'body', id: p.id },
    });
    firstPlanet = false;
    if (stops.length > 5) break;
  }

  // A prominent constellation high in the sky, and its brightest star.
  const cons = e.cons.constellations
    .map((c, k) => ({ c, k, v: e.frame.toEnu(e.consGeo.anchors[k]) }))
    .filter((x) => x.c.rank <= 2 && x.v[2] > 0.55)
    .sort((a, b) => a.c.rank - b.c.rank || b.v[2] - a.v[2]);
  if (cons[0]) {
    const { c, k, v } = cons[0];
    const cat = e.stars;
    const brightest = cat.named.find((i) => cat.info.get(i)?.con === c.id);
    const bname = brightest !== undefined ? starName(cat, brightest) : '';
    const ar = arabic ? ARABIC_STAR_NAMES[bname] : undefined;
    const meaning = constellationMeaning(c);
    stops.push({
      title: c.name,
      text: `High overhead is ${c.name}${meaning ? `, the ${meaning}` : ''}. ${bname ? `Its brightest star is ${bname}${ar ? (ar.meaning && ar.fromArabic !== false ? `, from the Arabic ${ar.tr} (${ar.ar}), “${ar.meaning}”.` : `, known in Arabic as ${ar.tr} (${ar.ar})${ar.meaning ? `, “${ar.meaning}”` : ''}.`) : '.'}` : ''}`,
      clips: [clip.constellation(c.id, c.name, meaning).key, ...(bname ? [clip.constellationStar(c.id, bname).key] : [])],
      enu: v, fov: 60, sel: { kind: 'constellation', index: k },
    });
  }

  // The brightest star that is up.
  const cat = e.stars;
  for (const i of cat.named) {
    const v = e.frame.toEnu(e.starDir(i));
    if (!up(v)) continue;
    const name = starName(cat, i);
    const info = cat.info.get(i);
    const ly = info?.ly ?? 0;
    const far = ly > 0 ? clip.starDistance(name, ly) : null;
    const ar = arabic ? ARABIC_STAR_NAMES[name] : undefined;
    const clips = [clip.star(name).key, loc(v)];
    if (far) clips.push(far.key);
    stops.push({
      title: name,
      text: `The brightest star up now is ${name}, ${where(v)}.${far ? ` ${far.text}` : ''}${ar ? ` In Arabic: ${ar.ar} (${ar.tr})${ar.meaning ? `, “${ar.meaning}”` : ''}.` : ''}`,
      clips, enu: v, fov: 25, sel: { kind: 'star', index: i },
    });
    break;
  }

  // A showpiece deep-sky object.
  for (const id of ['M31', 'M45', 'M42', 'M13', 'M44', 'M7']) {
    const k = e.dsos.findIndex((d) => d.id === id);
    if (k < 0) continue;
    const v = e.frame.toEnu(e.dsos[k].dir);
    if (!up(v)) continue;
    // With Arabic on, the Pleiades' Arabic name on screen; the voice says the plain line (rule G9).
    const shown = clip.dso(id, arabic)!, said = clip.dso(id)!;
    stops.push({ title: e.dsos[k].name ?? id, text: `${shown.text} It is ${where(v)}.`, clips: [said.key, loc(v)], enu: v, fov: 20, sel: { kind: 'dso', index: k } });
    break;
  }

  // The next bright satellite pass tonight.
  try {
    const passes = await s.sats.passes('notable', date, 6, 3);
    const p = passes.find((x) => x.visStart && x.visStart > date.getTime());
    if (p) {
      const meta = s.sats.meta.get(p.id);
      const name = meta ? displayName(meta) : p.name;
      const kind = p.id === 25544 ? 'iss' : p.id === 48274 ? 'tiangong' : p.id === 20580 ? 'hubble' : 'other';
      const t = fmtTime(p.visStart!);
      stops.push({
        title: `${name} at ${t}`,
        text: `${name === 'ISS' ? 'The International Space Station' : name} passes over at ${t}, appearing in the ${DIRS[Math.round(p.visStartAz / 45) % 8]} and climbing to ${Math.round(p.peakEl)}° for about ${Math.max(1, Math.round((p.visEnd! - p.visStart!) / 60000))} minutes.`,
        clips: [clip.satellite(kind).key], enu: null, fov: 110, sel: { kind: 'satellite', id: p.id },
      });
    }
  } catch {
    /* no satellite data */
  }

  // Meteors.
  const act = activeShowers(s.meteors.showers, date, 3);
  if (act[0]) {
    const a = act[0];
    const v = e.frame.toEnu(a.dir);
    const rate = hourlyRate(a.zhr, (Math.asin(v[2]) * 180) / Math.PI, a.shower.r, ctx.nelm);
    stops.push({
      title: a.shower.name,
      text: `The ${a.shower.name} are active. ${clip.showerRate(rate).text} ${clip.showerRadiant().text}`,
      clips: [clip.shower(a.shower.code, a.shower.name).key, clip.showerRate(rate).key, clip.showerRadiant().key],
      enu: v[2] > 0 ? v : null, fov: 90, sel: { kind: 'shower', code: a.shower.code },
    });
  }

  stops.push({ title: 'Clear skies', text: clip.outro().text, clips: [clip.outro().key], enu: null, fov: 110 });
  return stops;
}
